// The toolbar search (APPLE-SPEC §5.5; shell-ia §6; simplified 15 Oct 2026): one quiet glyph button with its ⌘K hint in the bar (the glyph
// alone on a phone). ⌘K, "/" or a click opens the results in a 460 px popover end-aligned under the bar — a sibling of the bar portalled
// to <body>, so its blur sees the page — with its own 36 px field at the top; on a phone it is full height under the strip with Cancel.
// The field is a combobox: it keeps focus while ↑ ↓ move the navy row (aria-activedescendant), ↵ opens, ⌘↵ asks ECONIC, Esc closes and
// gives focus back to the button. One popover at a time: opening search closes any menu. WP-SHELL.
import { lazy, Suspense, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import { useLocation } from "react-router";
import { useTranslation } from "@ncp/i18n";
import { MOD, type PaletteItem } from "@ncp/ui";
import { useMediaQuery } from "../../lib/media";
import type { SearchResultsHandle } from "./SearchResults";

const SearchResults = lazy(() => import("./SearchResults"));

export function ToolbarSearch({ open, onOpenChange, actions }: { open: boolean; onOpenChange: (open: boolean) => void; actions: PaletteItem[] }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const phone = !useMediaQuery("(min-width: 768px)");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const [place, setPlace] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const headInput = useRef<HTMLInputElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const results = useRef<SearchResultsHandle>(null);
  const listId = `es-search-${useId().replace(/:/g, "")}`;

  const close = useCallback((focusBack = true) => {
    onOpenChange(false);
    setQuery("");
    setActive(null);
    if (focusBack) button.current?.focus({ preventScroll: true });
    else if (document.activeElement instanceof HTMLElement && pop.current?.contains(document.activeElement)) document.activeElement.blur();
  }, [onOpenChange]);

  // Opening (⌘K, "/", a click) focuses the field that is on screen.
  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => headInput.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Navigation closes it.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    onOpenChange(false);
    setQuery("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // A pointer or focus outside the field and the popover closes it; so does a menu of the bar opening (one popover at a time).
  useEffect(() => {
    if (!open) return;
    const inside = (n: EventTarget | null) => n instanceof Node && (!!pop.current?.contains(n) || !!button.current?.contains(n));
    const onDown = (e: PointerEvent) => { if (!inside(e.target)) close(false); };
    const onFocus = (e: FocusEvent) => { if (!inside(e.target)) close(false); };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("focusin", onFocus);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("focusin", onFocus);
    };
  }, [open, close]);

  // The popover anchors under the bar, end-aligned with the button, and re-anchors on resize.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const anchor = button.current?.getBoundingClientRect();
      const bar = document.querySelector("[data-econic-topbar]")?.getBoundingClientRect();
      if (!anchor) return;
      const top = (bar?.bottom ?? anchor.bottom) + 6;
      const rtl = document.documentElement.dir === "rtl";
      setPlace(rtl ? { top, left: Math.max(8, anchor.left) } : { top, right: Math.max(8, window.innerWidth - anchor.right) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") onOpenChange(true);
      return;
    }
    results.current?.onKey(e);
  };

  const label = t("assistant:econic.shell.search.open");
  const fieldProps = (ref: typeof headInput) => ({
    ref,
    type: "search" as const,
    role: "combobox" as const,
    "aria-label": label,
    "aria-expanded": open,
    "aria-controls": open ? listId : undefined,
    "aria-activedescendant": open && active ? active : undefined,
    "aria-autocomplete": "list" as const,
    autoComplete: "off",
    spellCheck: false,
    enterKeyHint: "search" as const,
    placeholder: t("assistant:econic.shell.search.button"),
    value: query,
    onChange: (e: ChangeEvent<HTMLInputElement>) => { setQuery(e.target.value); if (!open) onOpenChange(true); },
    onFocus: () => { if (!open) onOpenChange(true); },
    onKeyDown: onKey,
  });
  const clear = () => (
    <button type="button" className="clr" aria-label={t("assistant:econic.shell.search.clear")} title={t("assistant:econic.shell.search.clear")}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => { if (query) { setQuery(""); headInput.current?.focus(); } else close(true); }}>
      <X aria-hidden />
    </button>
  );

  return (
    <>
      <button ref={button} type="button" className="es-icon-btn es-search-btn" onClick={() => (open ? close(false) : onOpenChange(true))}
        aria-label={label} title={label} aria-expanded={open} aria-haspopup="listbox" aria-keyshortcuts="Meta+K Control+K /" data-econic-search-btn="" data-econic-search="">
        <Search aria-hidden />
        <kbd aria-hidden>{MOD}K</kbd>
      </button>
      {open && (place || phone)
        ? createPortal(
            <div ref={pop} className="es-spop" data-head="" style={phone ? undefined : { top: place!.top, left: place!.left, right: place!.right }} data-econic-search-pop="">
              <div className="sp-head">
                <div className="es-search" data-open="">
                  <Search aria-hidden />
                  <input {...fieldProps(headInput)} />
                  {clear()}
                </div>
                <button type="button" className="cancel" onClick={() => close(true)}>{t("assistant:econic.shell.search.cancel")}</button>
              </div>
              <Suspense fallback={<div className="skel" aria-hidden><span /><span /><span /></div>}>
                <SearchResults ref={results} query={query} listId={listId} actions={actions} onActive={setActive} onClose={close} />
              </Suspense>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
